import {
  type MutableRefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'

import { getAbsoluteOffset } from '@lib/dom'
import {
  type CombinedListingStep,
  type CombinedListingToggleStepHandler,
  getCombinedListingProductDropdownOptions,
  ProductCombinedListingContext,
} from '@lib/product/combined-listing'
import { getOptionTitle } from '@lib/product/option'
import { ProductQuickAddContext } from '@lib/product/quick-add-context'
import { SiteContext } from '@lib/site-context'
import { getSizeDimensionsFromString } from '@lib/size'
import { StringsContext } from '@lib/strings-context'

import Accordion from '@components/accordion'
import RangeSlider from '@components/range-slider'
import SimplePortableText from '@components/simple-portable-text'
import OptionListItem from './combined-listing-option-list-item'

interface CombinedListingProductPassepartoutSelectProps {
  passepartoutHoleWidth: number | null
  passepartoutHoleHeight: number | null
  isDefaultSelectionLoadedRef: MutableRefObject<boolean>
  passepartoutProductId?: number
  openStep?: CombinedListingStep
  isScreenSmallOrLarger: boolean
  setPassepartoutHoleWidth: (width: number | null) => void
  setPassepartoutHoleHeight: (height: number | null) => void
  onToggleStep: CombinedListingToggleStepHandler
}

export const PASSEPARTOUT_MIN_VALUE = 1

const CombinedListingProductPassepartoutSelect = ({
  passepartoutHoleWidth,
  passepartoutHoleHeight,
  isDefaultSelectionLoadedRef,
  passepartoutProductId,
  openStep,
  isScreenSmallOrLarger,
  setPassepartoutHoleWidth,
  setPassepartoutHoleHeight,
  onToggleStep,
}: CombinedListingProductPassepartoutSelectProps) => {
  const {
    combinedListingConfiguration,
    combinedListingActiveSize,
    combinedListingActivePassepartoutHoleSize,
    addCombinedListingProduct,
    clearCombinedListingProductType,
    setCombinedListingPassepartoutHoleSize,
  } = useContext(ProductCombinedListingContext)
  const { productQuickAdd } = useContext(ProductQuickAddContext)
  const { headerHeight } = useContext(SiteContext)
  const strings = useContext(StringsContext)

  const ref = useRef<HTMLDivElement | null>(null)

  const sizeDimensions = useMemo(
    () => getSizeDimensionsFromString(combinedListingActiveSize ?? ''),
    [combinedListingActiveSize],
  )

  const passepartoutHoleSizeDimensions = useMemo(
    () =>
      getSizeDimensionsFromString(
        combinedListingActivePassepartoutHoleSize ?? '',
      ),
    [combinedListingActivePassepartoutHoleSize],
  )

  const passepartoutOptions = useMemo(
    () =>
      getCombinedListingProductDropdownOptions(
        combinedListingConfiguration?.passepartout ?? [],
        combinedListingActiveSize ?? null,
        strings.combinedListingNoPassepartout,
        strings.combinedListingNoPassepartoutDescription,
      ),
    [combinedListingActiveSize, combinedListingConfiguration, strings],
  )

  const hasPassepartoutOptions = useMemo(
    () => passepartoutOptions.some((option) => !!option.value),
    [passepartoutOptions],
  )

  const updateCombinedListingPassepartoutHoleSize = useCallback(() => {
    const newPassepartoutHoleWidth =
      passepartoutHoleWidth !== null
        ? passepartoutHoleWidth
        : passepartoutHoleSizeDimensions.width
    const newPassepartoutHoleHeight =
      passepartoutHoleHeight !== null
        ? passepartoutHoleHeight
        : passepartoutHoleSizeDimensions.height

    setCombinedListingPassepartoutHoleSize(
      `${newPassepartoutHoleWidth}x${newPassepartoutHoleHeight}`,
    )
  }, [
    passepartoutHoleWidth,
    passepartoutHoleHeight,
    passepartoutHoleSizeDimensions,
    setCombinedListingPassepartoutHoleSize,
  ])

  const handlePassepartoutHoleSizeChange = useCallback(() => {
    isDefaultSelectionLoadedRef.current = true

    updateCombinedListingPassepartoutHoleSize()
  }, [isDefaultSelectionLoadedRef, updateCombinedListingPassepartoutHoleSize])

  const handlePassepartoutChange = useCallback(
    (newProductId: string) => {
      isDefaultSelectionLoadedRef.current = true

      if (!newProductId) {
        clearCombinedListingProductType('passepartout')
        setCombinedListingPassepartoutHoleSize(null)

        return
      }

      updateCombinedListingPassepartoutHoleSize()
      addCombinedListingProduct(Number(newProductId))
    },
    [
      addCombinedListingProduct,
      clearCombinedListingProductType,
      isDefaultSelectionLoadedRef,
      setCombinedListingPassepartoutHoleSize,
      updateCombinedListingPassepartoutHoleSize,
    ],
  )

  const handleToggle = useCallback(
    (isOpen: boolean) => {
      onToggleStep('passepartout', isOpen)

      // Scrolling animation on mobile when opening the accordion
      if (isOpen && !isScreenSmallOrLarger && !productQuickAdd.isOpen) {
        setTimeout(() => {
          if (!ref.current) {
            return
          }

          const offsetTop = getAbsoluteOffset(ref.current).top
          window.scroll({
            top: Math.max(0, offsetTop - headerHeight),
            behavior: 'smooth',
          })
        }, 500)
      }
    },
    [headerHeight, isScreenSmallOrLarger, onToggleStep, productQuickAdd.isOpen],
  )

  if (!hasPassepartoutOptions) {
    return null
  }

  return (
    <Accordion
      ref={ref}
      id="combined-listing-product-backboard"
      title={
        <>
          {strings.combinedListingPassepartout}:{' '}
          <span className="!font-normal">
            {getOptionTitle(
              passepartoutOptions,
              `${passepartoutProductId ?? ''}`,
            )}
          </span>
        </>
      }
      bordered
      open={openStep === 'passepartout'}
      onToggle={handleToggle}
      innerClassName="space-y-5"
    >
      <div className="space-y-3">
        {strings.combinedListingPassepartoutDescription && (
          <SimplePortableText
            content={strings.combinedListingPassepartoutDescription}
            className="[&>*]:text-sm [&>*]:leading-[1.4] [&>*]:tracking-[2%]"
          />
        )}

        <div className="flex flex-col gap-2.5">
          {passepartoutOptions.map((option) => (
            <OptionListItem
              key={option.value}
              title={option.title}
              description={option.description}
              price={option.price}
              alt={option.title}
              photo={option.imageUrl}
              active={`${passepartoutProductId ?? ''}` === option.value}
              onClick={() => handlePassepartoutChange(option.value)}
            />
          ))}
        </div>
      </div>

      {!!passepartoutProductId && (
        <div className="space-y-1">
          <label className="text-sm font-medium">
            {strings.combinedListingPassepartoutHoleSize}
          </label>

          <div className="space-y-2">
            <RangeSlider
              label="Width (cm)"
              value={
                passepartoutHoleWidth ?? passepartoutHoleSizeDimensions.width
              }
              setValue={setPassepartoutHoleWidth}
              onMouseUp={handlePassepartoutHoleSizeChange}
              onTouchEnd={handlePassepartoutHoleSizeChange}
              onBlur={handlePassepartoutHoleSizeChange}
              min={PASSEPARTOUT_MIN_VALUE}
              max={sizeDimensions.width - PASSEPARTOUT_MIN_VALUE}
            />

            <RangeSlider
              label="Height (cm)"
              value={
                passepartoutHoleHeight ?? passepartoutHoleSizeDimensions.height
              }
              setValue={setPassepartoutHoleHeight}
              onMouseUp={handlePassepartoutHoleSizeChange}
              onTouchEnd={handlePassepartoutHoleSizeChange}
              onBlur={handlePassepartoutHoleSizeChange}
              min={PASSEPARTOUT_MIN_VALUE}
              max={sizeDimensions.height - PASSEPARTOUT_MIN_VALUE}
            />
          </div>
        </div>
      )}
    </Accordion>
  )
}

export default CombinedListingProductPassepartoutSelect
